import { Fragment, useEffect, useState } from 'react'
import { tileFor } from '../components/signup/docSets'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AppLayout } from '../components/AppLayout'
import AdminMarketing from '../components/AdminMarketing'
import { AdminOrgs } from '../components/AdminOrgs'
import { AdminOrders } from '../components/AdminOrders'
import { AdminSourcingDesk } from '../components/AdminSourcingDesk'
import { canUseAgenticSourcing } from '../lib/sourcing'
import {
  adminApi,
  type AdminSummary,
  type AdminUser,
} from '../api/endpoints'
import { api } from '../api/client'
import { ApiError } from '../api/types'
import { watchAuthState } from '../lib/firebase'

/**
 * /admin — the v0 console: three tabs, nothing else (Tej 2026-09-20).
 *
 *   1. Users & KYC — COMPANY-FIRST verification (approve a company once,
 *      members inherit; individual brokers keep their own per-person KYC in
 *      the People lens) + roles + org markup mode.
 *   2. Orders — read-only orders + enquiries (the v0 deal-flow signal).
 *   3. Marketing — campaigns, with the platform stats strip folded in.
 *
 * Role gate: server enforces requireRole('admin') on every route; the client
 * additionally redirects non-admins away from an empty/forbidden page.
 */

type Tab = 'users' | 'orders' | 'marketing' | 'sourcing'

// The Sourcing Desk (docs/ASKA-PLUS-AGENTIC-SOURCING.md §13) is FLAG-GATED: it only
// joins the console when SOURCING_RFQ_LIVE is armed, so the v0 3-tab console is
// unchanged (no new surface, no read-only-sweep delta) until the feature turns on.
const TABS: Tab[] = ['users', 'orders', 'marketing', ...(canUseAgenticSourcing() ? ['sourcing' as Tab] : [])]

/** Old deep-links keep landing somewhere sensible: Stats folded into
 *  Marketing (2026-09-20), so `?tab=analytics` opens the Marketing tab. */
const TAB_ALIASES: Record<string, Tab> = { analytics: 'marketing' }

export function Admin() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // `?tab=` deep-links a tab; retired tab names alias to their new home.
  const initialTab = params.get('tab')
  const [tab, setTab] = useState<Tab>(
    initialTab && (TABS as string[]).includes(initialTab)
      ? (initialTab as Tab)
      : (initialTab && TAB_ALIASES[initialTab]) || 'users',
  )
  const [authChecked, setAuthChecked] = useState(false)
  const [allowed, setAllowed] = useState(false)
  // Real signed-in admin identity for the sidebar chip (it used to show a
  // hardcoded "Admin User / admin@diaex.com" placeholder).
  const [adminEmail, setAdminEmail] = useState<string | null>(null)

  // Soft client gate — server is authoritative, but redirect non-admins
  // away so they don't see an empty/forbidden page.
  // Wait for Firebase auth state to hydrate before calling /me; otherwise
  // a hard refresh on /admin races the API call against the auth token.
  // Retry once on 401 because the token can take a few hundred ms to be
  // ready even after currentUser is set (refresh token exchange).
  useEffect(() => {
    let cancelled = false

    async function checkAdmin(user: import('firebase/auth').User) {
      // Read role directly from the ID token's custom claims rather than
      // /me — Heera-migrated users have a Firestore stub at users/{uid}
      // (mobile registration) that shadows their `users/{userId}` admin
      // doc, so /me may incorrectly return role=buyer. The claim is the
      // source of truth.
      try {
        const tok = await user.getIdTokenResult()
        const claims = tok.claims as { role?: string; isAdmin?: boolean }
        const isAdmin = claims.role === 'admin' || !!claims.isAdmin
        if (cancelled) return
        setAllowed(isAdmin)
        setAdminEmail(user.email ?? null)
        setAuthChecked(true)
        if (!isAdmin) {
          setTimeout(() => navigate('/'), 800)
        }
      } catch (err) {
        if (cancelled) return
        setAllowed(false)
        setAuthChecked(true)
        navigate('/signin?next=/admin')
      }
    }

    const unsub = watchAuthState((user) => {
      if (cancelled) return
      if (!user) {
        navigate('/signin?next=/admin')
        return
      }
      checkAdmin(user)
    })
    return () => { cancelled = true; unsub() }
  }, [navigate])

  if (!authChecked) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-5xl px-6 py-10">
          <div className="rounded-3xl bg-paper border border-border h-44 animate-pulse" />
        </div>
      </AppLayout>
    )
  }

  if (!allowed) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-5xl px-6 py-10">
          <div className="rounded-card border border-warn/40 bg-warn/10 text-warn text-sm p-4">
            This page is admin-only. Redirecting…
          </div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1400px] w-full flex flex-col lg:flex-row gap-6 px-4 sm:px-6 py-6">
        <AdminSidebar tab={tab} setTab={setTab} email={adminEmail} />

        <div className="min-w-0 flex-1 space-y-5">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink tracking-tight">Admin console</h1>
          <p className="mt-1 text-sm text-muted">
            Approve companies, watch orders &amp; enquiries, run marketing.
          </p>
        </div>

        <KpiHeader />

        {/* The horizontal tab row is the BELOW-lg navigation only — on desktop
            the navy sidebar is the single nav (two parallel navs driving the
            same tab state read as clutter). Right-edge fade cues that the row
            scrolls horizontally at 390px. */}
        <div className="relative -mx-4 sm:mx-0 lg:hidden">
        <div className="pointer-events-none absolute right-0 top-0 bottom-px w-8 bg-gradient-to-l from-page to-transparent lg:hidden" aria-hidden />
        <div className="flex items-center gap-1 border-b border-border overflow-x-auto px-4 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="admin-tabs">
          <TabBtn active={tab === 'users'} onClick={() => setTab('users')} testId="admin-tab-users">
            Users &amp; KYC
          </TabBtn>
          <TabBtn active={tab === 'orders'} onClick={() => setTab('orders')} testId="admin-tab-orders">
            Orders
          </TabBtn>
          <TabBtn active={tab === 'marketing'} onClick={() => setTab('marketing')} testId="admin-tab-marketing">
            Marketing
          </TabBtn>
          {canUseAgenticSourcing() && (
            <TabBtn active={tab === 'sourcing'} onClick={() => setTab('sourcing')} testId="admin-tab-sourcing">
              Sourcing Desk
            </TabBtn>
          )}
        </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            {tab === 'users' ? <UsersCard />
              : tab === 'orders' ? <AdminOrders />
                : tab === 'sourcing' ? <AdminSourcingDesk />
                  : <AdminMarketing />}
          </motion.div>
        </AnimatePresence>
        </div>
      </div>
    </AppLayout>
  )
}

/**
 * Navy admin sidebar (premium console chrome). Serif "DiaEx" wordmark + ADMIN
 * label, icon nav that drives the same `tab` state the tab bar does, and an
 * admin identity chip at the bottom. Presentation only — every destination is
 * an existing tab. Collapses to a horizontal scroller on mobile.
 */
function AdminSidebar({ tab, setTab, email }: { tab: Tab; setTab: (t: Tab) => void; email?: string | null }) {
  // Labels MUST say where the click lands (the operator is non-technical):
  // 'chat' used to be labelled "Analytics" and 'payments' "Settings" — both
  // opened something other than what they promised. Names now mirror the
  // tab row 1:1, and the dormant credit tabs (Payments / Limits) are both here.
  const items: Array<{ tab: Tab; label: string; icon: React.ReactNode }> = [
    { tab: 'users', label: 'Users & KYC', icon: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20a6 6 0 0 1 12 0M16 6.5a3 3 0 0 1 0 5.8M21 20a5.5 5.5 0 0 0-4-5.3" /></> },
    { tab: 'orders', label: 'Orders', icon: <path d="M4 7h16M4 7l2-3h12l2 3M4 7v13h16V7M9 11h6" /> },
    { tab: 'marketing', label: 'Marketing', icon: <path d="m3 11 18-8-8 18-2-8-8-2Z" /> },
    // Sourcing Desk — only when SOURCING_RFQ_LIVE (docs/ASKA-PLUS-AGENTIC-SOURCING.md §13).
    ...(canUseAgenticSourcing()
      ? [{ tab: 'sourcing' as Tab, label: 'Sourcing Desk', icon: <path d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM21 21l-4.3-4.3" /> }]
      : []),
  ]
  return (
    <aside
      className="lg:w-60 lg:shrink-0 lg:sticky lg:top-6 lg:self-start rounded-card bg-navy text-white overflow-hidden shadow-soft"
      aria-label="Admin navigation"
      data-testid="admin-sidebar"
    >
      {/* On mobile the navy sidebar shows only the branded header banner —
          the horizontal tab row below the KPIs is the single source of
          navigation. Rendering the section nav here too would give two
          horizontally-scrolling rows driving the same `tab` state (with
          mismatched labels), which read as redundant and cramped at 390px. */}
      <div className="px-5 pt-5 pb-4 lg:border-b lg:border-white/10">
        <p className="font-display text-2xl leading-none text-white">DiaEx</p>
        <p className="mt-1 text-[10px] uppercase tracking-[0.28em] text-gold-soft">Admin</p>
      </div>
      <nav className="hidden lg:flex p-2 flex-col gap-1">
        {items.map((it) => {
          const active = tab === it.tab
          return (
            <button
              key={it.label}
              type="button"
              onClick={() => setTab(it.tab)}
              data-testid={`admin-side-${it.tab}`}
              className={`flex items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                active ? 'bg-paper/10 text-white' : 'text-white/70 hover:bg-paper/5 hover:text-white'
              }`}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-gold' : 'text-white/50'}>
                {it.icon}
              </svg>
              {it.label}
            </button>
          )
        })}
      </nav>
      {/* Identity chip — the REAL signed-in account (was a hardcoded
          "Admin User / admin@diaex.com" placeholder with a decorative
          chevron that promised a menu that didn't exist). */}
      <div className="hidden lg:flex items-center gap-2.5 mx-2 mb-2 mt-1 px-3 py-2.5 rounded-lg border-t border-white/10">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-paper/10 text-xs font-semibold text-gold-soft">
          {(email || 'A').charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-white truncate">Signed in as admin</p>
          <p className="text-[10px] text-white/50 truncate">{email || '—'}</p>
        </div>
      </div>
    </aside>
  )
}

function TabBtn({ active, onClick, children, testId }: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  testId?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`whitespace-nowrap px-4 py-2.5 text-sm font-semibold transition-colors ${
        active
          ? 'text-ink border-b-2 border-line -mb-px'
          : 'text-soft hover:text-ink'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * KPI header — the four numbers that answer "what's on the platform right now":
 * total stones, users, enquiries logged, and registered merchants.
 * Backed by GET /admin/summary (Firestore count() aggregations).
 *
 * 2026-08-02: the hardcoded "▲ 8.4% vs last 7 days"-style trend captions were
 * REMOVED — no trend API exists, so they were invented numbers presented as
 * live data. A daily operator reads these cells as truth; a made-up figure in
 * an ops console is worse than no figure.
 */
function KpiHeader() {
  const [data, setData] = useState<AdminSummary | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    adminApi.summary()
      .then((r) => { if (!cancelled) setData(r) })
      .catch(() => { if (!cancelled) setError(true) })
    return () => { cancelled = true }
  }, [])

  const cells: Array<{
    label: string
    caption: string
    value: number | null
    icon: React.ReactNode
  }> = [
    {
      label: 'Stones', caption: 'Total listed', value: data?.stones ?? null,
      icon: <path d="M12 2 3 8.5 12 22l9-13.5L12 2Zm0 0v20M3 8.5h18" />,
    },
    {
      label: 'Users', caption: 'Approved users', value: data?.users ?? null,
      icon: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20a6 6 0 0 1 12 0M16 6.5a3 3 0 0 1 0 5.8M21 20a5.5 5.5 0 0 0-4-5.3" /></>,
    },
    {
      label: 'Enquiries', caption: 'Open enquiries', value: data?.enquiries ?? null,
      icon: <path d="M4 5h16v11H8l-4 4V5Z" />,
    },
    {
      label: 'Merchants', caption: 'Registered merchants', value: data?.merchantCount ?? null,
      icon: <path d="M4 9h16l-1-4H5L4 9Zm0 0v11h16V9M9 20v-5h6v5" />,
    },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-kpi">
      {cells.map((c) => (
        <div key={c.label} className="card px-4 py-3.5 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[10px] uppercase tracking-[0.14em] text-soft">{c.label}</p>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="text-gold shrink-0">
              {c.icon}
            </svg>
          </div>
          <p className="mt-2 font-display text-2xl font-bold text-ink tabular-nums leading-none">
            {error ? '—' : c.value == null ? '…' : c.value.toLocaleString()}
          </p>
          <p className="mt-1.5 text-xs text-muted">{c.caption}</p>
        </div>
      ))}
    </div>
  )
}

// ─── Increment 1: re-surfaced backends ──────────────────────────────────────

const roleLabel = (u: AdminUser): string =>
  (u.role as string) || (u.userType === 1 ? 'admin' : u.userType === 2 ? 'supplier' : u.userType === 3 ? 'buyer' : '—')

const userName = (u: AdminUser): string =>
  (u.contactPerson as string) || (u.companyName as string) || (u.displayName as string) || (u.email as string) || u.uid

/**
 * Users & KYC — TWO LENSES ON ONE QUEUE.
 *
 * **People** (the original) is the per-PERSON decision surface: approve or
 * reject an individual, change their platform role, read their wizard answers.
 *
 * **Organizations** (step 8) is the per-COMPANY one, and it is new because
 * `POST /admin/orgs/:orgId/kyc` had shipped with no UI at all — dad, who IS the
 * KYC approver, could not approve a company. Since the whole point of org
 * accounts is that verification is inherited (approve the company once, every
 * member is productive immediately), that missing button was the product.
 *
 * They are lenses rather than tabs because they answer the same question about
 * different subjects, and a reviewer moves between them constantly: the org
 * chip in a person's row opens their company, and a name in the company's
 * member list comes back here. Per docs/ORG-ACCOUNTS.md: a grant flows UP from
 * the owner to the company, a deny stays WITH the person — so which lens you
 * are in is exactly the difference between "this person's papers are bad" and
 * "shut this company down".
 */
function UsersCard() {
  // COMPANY-FIRST (Tej 2026-09-20): KYC is a per-COMPANY decision — approve
  // the company once and every member inherits — so Companies is the default
  // review queue. People stays first-class for the one population whose KYC
  // really is personal: INDIVIDUAL BROKERS (their own documents, their own
  // decision), plus role changes and per-person doc review.
  const [lens, setLens] = useState<'people' | 'orgs'>('orgs')
  const [focusOrgId, setFocusOrgId] = useState<string | null>(null)
  const [focusPersonKey, setFocusPersonKey] = useState<string | null>(null)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1 rounded-full border border-line bg-paper p-1 w-fit" role="tablist">
        {([['orgs', 'Companies'], ['people', 'People']] as const).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={lens === k}
            onClick={() => setLens(k)}
            data-testid={`admin-users-lens-${k}`}
            className={`relative rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
              lens === k ? 'text-white' : 'text-navy-500 hover:text-ink'
            }`}
          >
            {lens === k && (
              <motion.span
                layoutId="admin-users-lens"
                className="absolute inset-0 rounded-full bg-navy-900"
                transition={{ type: 'spring', stiffness: 340, damping: 34 }}
              />
            )}
            <span className="relative">{label}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted">
        {lens === 'orgs'
          ? 'Approve a company once — every member inherits. Markup mode lives here too.'
          : 'Individual brokers are approved HERE (their own KYC); company members are approved via Companies.'}
      </p>
      </div>
      {lens === 'people'
        ? <PeopleCard
            focusKey={focusPersonKey}
            onOpenOrg={(orgId) => { setFocusOrgId(orgId); setLens('orgs') }}
          />
        : <AdminOrgs
            focusOrgId={focusOrgId}
            onOpenPerson={(uid) => { setFocusPersonKey(uid); setLens('people') }}
          />}
    </div>
  )
}

/* ── WS-M: the company name/address change-request review queue ─────────────
 *
 * An org owner/admin cannot rewrite their company's legal name or address
 * directly — they submit a REQUEST with proof, which staff approve (the value
 * is applied to the company) or reject here. Proof documents open through the
 * SAME reviewer URL KYC docs use (`adminApi.kycDocUrl`, whose path regex admits
 * `company-proof/`). Empty ⇒ the panel collapses to a single quiet line so it
 * never shouts at an operator with nothing to do.
 */
interface AdminChangeRequest {
  id: string
  orgId: string
  field: 'legalName' | 'address' | 'displayName' | 'website' | 'registrationNumbers' | 'contactPhone'
  proposedValue: string | null | Record<string, unknown>
  proof: Array<{ objectPath: string; fileName: string }>
  status: 'pending' | 'approved' | 'rejected'
  submittedBy: string
  submittedAt: string
}

const CHANGE_FIELD_LABELS: Record<AdminChangeRequest['field'], string> = {
  legalName: 'Legal name',
  address: 'Address',
  displayName: 'Trading name',
  website: 'Website',
  registrationNumbers: 'Registration numbers',
  contactPhone: 'Contact number',
}

/** Human-readable proposed value for the review row — handles the address shape,
 *  the registration-numbers object, and a cleared (null/empty) value. */
function changeRequestValueText(r: AdminChangeRequest): string {
  if (r.field === 'address') return changeAddressLine(r.proposedValue)
  if (r.field === 'registrationNumbers' && r.proposedValue && typeof r.proposedValue === 'object') {
    const parts = Object.entries(r.proposedValue as Record<string, unknown>)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${k}: ${String(v)}`)
    return parts.length ? parts.join(', ') : '(cleared)'
  }
  return r.proposedValue == null || r.proposedValue === '' ? '(cleared)' : String(r.proposedValue)
}

function OrgChangeRequestsPanel({ openDoc }: { openDoc: (objectPath: string) => void }) {
  const [requests, setRequests] = useState<AdminChangeRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    try {
      const r = await api<{ requests: AdminChangeRequest[] }>('/admin/org-change-requests')
      setRequests(r.requests || [])
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load change requests')
    } finally { setLoading(false) }
  }
  useEffect(() => { void load() }, [])

  async function decide(id: string, approve: boolean) {
    setWorking(id); setFlash(null); setError(null)
    try {
      await api(`/admin/org-change-requests/${encodeURIComponent(id)}/${approve ? 'approve' : 'reject'}`, { method: 'POST', body: {} })
      setRequests((prev) => prev.filter((r) => r.id !== id))
      setFlash(approve ? 'Change approved and applied to the company.' : 'Change request rejected.')
    } catch (e) {
      const body = (e as ApiError)?.body as { code?: string } | undefined
      setError(body?.code === 'already_reviewed' ? 'That request was already decided — refreshing.'
        : (e instanceof ApiError ? e.message : 'Could not record that decision.'))
      if (body?.code === 'already_reviewed') void load()
    } finally { setWorking(null) }
  }

  if (loading) return null
  if (requests.length === 0) {
    return (
      <p className="text-xs text-soft" data-testid="admin-change-requests-empty">
        No pending company change requests.{error ? ` (${error})` : ''}
      </p>
    )
  }

  return (
    <section className="rounded-card border border-warning/30 bg-warning/[0.06] p-3 space-y-2" data-testid="admin-change-requests">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-ink">
        Company change requests — {requests.length} awaiting review
      </h3>
      {flash && <div className="rounded-md bg-success/10 border border-success/30 text-success text-xs px-2 py-1">{flash}</div>}
      {error && <div className="rounded-md bg-danger/5 border border-danger/30 text-danger text-xs px-2 py-1">{error}</div>}
      <ul className="space-y-2">
        {requests.map((r) => (
          <li key={r.id} className="rounded-md border border-line bg-paper p-3 text-sm" data-testid={`admin-change-request-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-soft">
                  {CHANGE_FIELD_LABELS[r.field] ?? r.field} · org {r.orgId}
                </div>
                <div className="mt-0.5 break-words font-semibold text-ink" data-testid={`admin-change-request-${r.id}-value`}>
                  {changeRequestValueText(r)}
                </div>
                <div className="mt-0.5 text-[11px] text-soft">submitted by {r.submittedBy}</div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => decide(r.id, true)}
                  disabled={working === r.id}
                  className="rounded-md bg-navy-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                  data-testid={`admin-change-request-${r.id}-approve`}
                >
                  {working === r.id ? '…' : 'Approve'}
                </button>
                <button
                  type="button"
                  onClick={() => decide(r.id, false)}
                  disabled={working === r.id}
                  className="rounded-md border border-line px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50"
                  data-testid={`admin-change-request-${r.id}-reject`}
                >
                  Reject
                </button>
              </div>
            </div>
            {r.proof.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-change-request-${r.id}-proof`}>
                {r.proof.map((p) => (
                  <button
                    key={p.objectPath}
                    type="button"
                    onClick={() => openDoc(p.objectPath)}
                    className="rounded-full border border-line px-2.5 py-1 text-[11px] text-action underline underline-offset-2"
                  >
                    {p.fileName}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** A one-line address for the review row. Best-effort — the reviewer opens the
 *  proof for detail. */
function changeAddressLine(value: unknown): string {
  if (!value || typeof value !== 'object') return String(value ?? '')
  const a = value as Record<string, unknown>
  return ['line1', 'line2', 'city', 'state', 'postalCode', 'country']
    .map((k) => (typeof a[k] === 'string' ? (a[k] as string).trim() : ''))
    .filter(Boolean)
    .join(', ')
}

/** The four KYC states the People list groups on — the SAME derivation the KYC
 *  pill renders (approved > submitted > rejected > pending). */
type KycState = 'submitted' | 'pending' | 'rejected' | 'approved'
function kycStateOf(u: AdminUser): KycState {
  if (u.kycApproved) return 'approved'
  if (u.kycStatus === 'submitted') return 'submitted'
  if (u.kycStatus === 'rejected') return 'rejected'
  return 'pending'
}
/** Sort rank: actionable review queue first. submitted(0) > pending(1) >
 *  rejected(2) > approved(3). */
const KYC_RANK: Record<KycState, number> = { submitted: 0, pending: 1, rejected: 2, approved: 3 }
/** The status-filter segmented control — 'all' plus one bucket per state. */
const KYC_FILTERS = ['all', 'submitted', 'pending', 'approved', 'rejected'] as const
type KycFilter = typeof KYC_FILTERS[number]

/**
 * The PEOPLE lens — the user list the backend always exposed
 * (GET /admin/users), with the KYC approve/reject (POST /admin/kyc) and
 * set-role (POST /admin/set-role) controls. Search is client-side over the
 * first 200 users the endpoint returns.
 */
function PeopleCard({ focusKey, onOpenOrg }: {
  focusKey?: string | null
  onOpenOrg?: (orgId: string) => void
}) {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [kycFilter, setKycFilter] = useState<KycFilter>('all')
  const [working, setWorking] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)
  const [openReview, setOpenReview] = useState<string | null>(null)
  // Per-user draft of the business WhatsApp number the admin is typing in.
  const [waDraft, setWaDraft] = useState<Record<string, string>>({})
  const [truncated, setTruncated] = useState(false)

  // Arriving from a company's member list: open that person's review row and
  // put their id in the search box, because `GET /admin/users` caps at 200 and
  // scrolling for a name you were just looking at is not navigation.
  useEffect(() => {
    if (!focusKey) return
    setOpenReview(focusKey)
    setQ(focusKey)
  }, [focusKey])

  async function openDoc(objectPath: string) {
    // Open the tab synchronously so the user-gesture isn't consumed by the
    // await below — a popup opened AFTER the fetch gets blocked by the browser.
    const win = window.open('', '_blank', 'noopener')
    try {
      const { url } = await adminApi.kycDocUrl(objectPath)
      if (win) win.location.href = url
      else window.location.href = url
    } catch (e: any) {
      if (win) win.close()
      setError(e instanceof ApiError ? e.message : (e?.message || 'Could not open document'))
    }
  }

  async function load() {
    setLoading(true)
    try {
      const r = await adminApi.users()
      setUsers(r.users)
      setTruncated(r.truncated === true)
      setError(null)
    } catch (e: any) {
      setError(e instanceof ApiError ? e.message : (e?.message || 'Could not load users'))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  function keyFor(u: AdminUser): string {
    // Prefer the explicit uid; fall back to email or the Heera userId so
    // set-role/KYC can resolve the same target the backend accepts.
    return u.uid || (u.email as string) || String(u.userId ?? '')
  }

  async function deleteFromAska(u: AdminUser) {
    const label = (u.email as string) || u.uid
    const typed = window.prompt(`Delete ${label} from Aska?\n\nThis removes their profile, documents and sign-in permanently (orders are kept). Type DELETE to confirm.`)
    if (typed !== 'DELETE') return
    const k = String(u.uid)
    setWorking(`del-${k}`)
    try {
      // The row's uid can be a numeric legacy doc id; the endpoint wants the
      // AUTH uid — prefer firebaseUid when the row carries one.
      const authUid = (u.firebaseUid as string) || String(u.uid)
      await adminApi.deleteUser(authUid)
      setFlash(`${label} deleted from Aska.`)
      await load()
    } catch (e) {
      const body = (e as ApiError)?.body as { code?: string; remedy?: string } | undefined
      setFlash(
        body?.code === 'org_managed' ? 'They belong to a company — remove them from it first (Companies lens), then delete.'
          : body?.code === 'last_owner' ? 'They are the sole OWNER of a company with other members — transfer ownership first.'
          : body?.code === 'target_is_admin' ? 'That account is an admin — demote it first, then delete.'
          : (e instanceof ApiError ? e.message : 'Delete failed — try again.'),
      )
    } finally { setWorking(null) }
  }

  async function decideKyc(u: AdminUser, approve: boolean) {
    const target = keyFor(u)
    setWorking(`kyc-${target}`)
    setFlash(null)
    try {
      const r = await adminApi.kyc(target, approve, approve ? undefined : 'Documents unclear — please resubmit.')
      setUsers((prev) => prev.map((x) => (keyFor(x) === target ? { ...x, kycApproved: approve } : x)))
      // NEVER report success for a decision the gate ignores. An organization
      // rejection (or a suspended seat) overrides an individual approval, so
      // the write lands and `verified` stays false — this used to flash "KYC
      // approved" while the buyer was still looking at locked prices.
      if (approve && r.effectiveVerified === false) {
        setError(r.warning || `KYC approved for ${userName(u)}, but the gate still denies them (${r.blockedBy || 'blocked'}).`)
      } else if (!approve && r.orgStillVerified) {
        // The mirror in the other direction: the person is denied, the COMPANY
        // is not. Use the org button if the intent was to shut it down.
        setFlash(`KYC rejected for ${userName(u)} — their organization is still verified.`)
      } else {
        setFlash(`KYC ${approve ? 'approved' : 'rejected'} for ${userName(u)}.`)
      }
    } catch (e: any) {
      setError(e instanceof ApiError ? e.message : (e?.message || 'KYC update failed'))
    } finally {
      setWorking(null)
    }
  }

  async function saveWhatsapp(u: AdminUser) {
    const target = keyFor(u)
    const number = (waDraft[target] || '').trim()
    if (!number) return
    setWorking(`wa-${target}`)
    setFlash(null); setError(null)
    try {
      const r = await adminApi.setSupplierWhatsapp(target, number)
      setUsers((prev) => prev.map((x) => (keyFor(x) === target ? { ...x, phone: r.number, phoneVerified: true } : x)))
      setWaDraft((d) => ({ ...d, [target]: '' }))
      setFlash(`WhatsApp set for ${userName(u)}: ${r.number} (verified).`)
    } catch (e: any) {
      const code = (e as ApiError)?.body && ((e as ApiError).body as { code?: string }).code
      setFlash(code === 'no_number' ? 'That number could not be read — use full international format, e.g. +14155550100.'
        : (e instanceof ApiError ? e.message : 'Could not set WhatsApp — try again.'))
    } finally { setWorking(null) }
  }

  async function changeRole(u: AdminUser, role: 'buyer' | 'supplier' | 'admin') {
    const target = keyFor(u)
    setWorking(`role-${target}`)
    setFlash(null)
    try {
      await adminApi.setRole(target, role)
      setUsers((prev) => prev.map((x) => (keyFor(x) === target ? { ...x, role } : x)))
      setFlash(`${userName(u)} set to ${role} — they must re-login for it to take effect.`)
    } catch (e: any) {
      setError(e instanceof ApiError ? e.message : (e?.message || 'Role update failed'))
    } finally {
      setWorking(null)
    }
  }

  if (loading) return <div className="card animate-pulse h-40" />
  if (error) return <div className="rounded-card border border-danger/30 bg-danger/5 text-danger text-sm p-4" data-testid="admin-users">{error}</div>

  const needle = q.trim().toLowerCase()
  const searched = needle
    ? users.filter((u) =>
      userName(u).toLowerCase().includes(needle) ||
      String(u.email || '').toLowerCase().includes(needle) ||
      String(u.userId || '').includes(needle) ||
      // uid + orgId so a jump from the org panel's member list lands on a row.
      String(u.uid || '').toLowerCase().includes(needle) ||
      String(u.orgId || '').toLowerCase().includes(needle))
    : users
  // KYC-status filter (segmented control). 'submitted' is the review queue.
  const filtered = kycFilter === 'all' ? searched : searched.filter((u) => kycStateOf(u) === kycFilter)
  // Sort a COPY so actionable KYC floats up: submitted → pending → rejected →
  // approved, tiebreak by most-recently-touched (submitted/updated) then name.
  const rows = [...filtered].sort((a, b) => {
    const r = KYC_RANK[kycStateOf(a)] - KYC_RANK[kycStateOf(b)]
    if (r !== 0) return r
    const ta = String(a.kycSubmittedAt || a.updatedAt || '')
    const tb = String(b.kycSubmittedAt || b.updatedAt || '')
    if (ta !== tb) return tb.localeCompare(ta)
    return userName(a).localeCompare(userName(b))
  })

  return (
    <section className="card space-y-3" data-testid="admin-users">
      {flash && <div className="rounded-md bg-success/10 border border-success/30 text-success text-sm px-3 py-2">{flash}</div>}
      {/* WS-M: the company name/address change-request queue sits atop the
          verification tab — deciding a rename/relocation is the same reviewer's
          job as deciding KYC. */}
      <OrgChangeRequestsPanel openDoc={openDoc} />
      <div className="flex items-center justify-between gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name / email / id…"
          className="h-10 w-full max-w-xs rounded-lg border border-border bg-paper px-3 text-sm text-ink placeholder:text-soft"
          data-testid="admin-users-search"
        />
        <span className="text-xs text-muted whitespace-nowrap">{rows.length} of {users.length}</span>
      </div>
      {/* KYC-status filter — 'submitted' is the review queue. Counts scan off
          the search-filtered set so the pill reflects what a click would show. */}
      <div className="flex flex-wrap items-center gap-1 rounded-full border border-line bg-paper p-1 w-fit" role="tablist">
        {KYC_FILTERS.map((f) => {
          const count = f === 'all' ? searched.length : searched.filter((u) => kycStateOf(u) === f).length
          return (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={kycFilter === f}
              onClick={() => setKycFilter(f)}
              data-testid={`admin-users-kyc-filter-${f}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold capitalize transition-colors ${
                kycFilter === f ? 'bg-navy-900 text-white' : 'text-navy-500 hover:text-ink'
              }`}
            >
              {f} <span className="tabular-nums opacity-70">{count}</span>
            </button>
          )
        })}
      </div>
      {truncated && (
        // The 200 cap is silent otherwise, and "the account isn't in the list"
        // reads as "the account doesn't exist" to a reviewer.
        <div className="rounded-md border border-warning/30 bg-warning/[0.07] px-3 py-2 text-xs text-ink">
          This list is capped at the first 200 accounts and is not paginated — a name you cannot find may still exist.
          Org members multiply rows, so the cap bites sooner as companies grow.
        </div>
      )}
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-soft border-b border-border">
              <th className="py-2 px-2 font-semibold">User</th>
              <th className="py-2 px-2 font-semibold">Organization</th>
              <th className="py-2 px-2 font-semibold">Type</th>
              <th className="py-2 px-2 font-semibold">Role</th>
              <th className="py-2 px-2 font-semibold">KYC</th>
              <th className="py-2 px-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const k = keyFor(u)
              return (
                <Fragment key={k || u.uid}>
                <tr className="border-b border-[#f0f2f6]" data-testid={`admin-user-${k}`}>
                  <td className="py-3 px-2">
                    <div className="font-semibold text-ink">{userName(u)}</div>
                    {u.email && <div className="text-xs text-muted">{u.email}</div>}
                  </td>
                  <td className="py-3 px-2">
                    {u.orgId ? (
                      <button
                        type="button"
                        onClick={() => onOpenOrg?.(String(u.orgId))}
                        className="group inline-flex items-center gap-1 text-left"
                        data-testid="admin-user-org"
                        title="Open this company’s review panel"
                      >
                        <span className="text-xs font-semibold text-navy-500 group-hover:text-gold-600 tabular">
                          {String(u.orgId).slice(0, 10)}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-soft">{u.orgRole || 'member'}</span>
                        {u.orgSeatStatus === 'suspended' && (
                          <span className="text-[10px] uppercase tracking-wider text-warning">suspended</span>
                        )}
                      </button>
                    ) : (
                      <span className="text-xs text-soft">—</span>
                    )}
                  </td>
                  <td className="py-3 px-2">
                    {u.accountType
                      ? <span className={`rounded-full text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${
                          u.accountType === 'supplier' ? 'bg-info/10 text-info'
                          : u.accountType === 'broker' ? 'bg-gold-050 text-gold-600'
                          : 'bg-page text-navy-500'}`}>
                          {u.accountType}
                        </span>
                      : <span className="text-xs text-soft">—</span>}
                  </td>
                  <td className="py-3 px-2">
                    <select
                      value={roleLabel(u)}
                      disabled={working === `role-${k}`}
                      onChange={(e) => changeRole(u, e.target.value as 'buyer' | 'supplier' | 'admin')}
                      className="h-8 rounded-md border border-border bg-paper px-2 text-xs text-ink"
                      data-testid="admin-user-role"
                    >
                      {['buyer', 'supplier', 'admin'].map((r) => <option key={r} value={r}>{r}</option>)}
                      {!['buyer', 'supplier', 'admin'].includes(roleLabel(u)) && <option value={roleLabel(u)}>{roleLabel(u)}</option>}
                    </select>
                  </td>
                  <td className="py-3 px-2">
                    {u.kycApproved
                      ? <span className="rounded-full bg-success/15 text-success text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Approved</span>
                      : u.kycStatus === 'submitted'
                        ? <span className="rounded-full bg-warning/15 text-warning text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Submitted</span>
                        : u.kycStatus === 'rejected'
                          ? <span className="rounded-full bg-danger/10 text-danger text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Rejected</span>
                          : <span className="rounded-full bg-page text-soft text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Pending</span>}
                  </td>
                  <td className="py-3 px-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setOpenReview(openReview === k ? null : k)}
                      className="bg-paper border border-border text-navy-500 text-xs font-semibold px-3 py-1.5 rounded-md hover:border-navy-300 mr-1"
                      data-testid="admin-user-review"
                    >
                      {openReview === k ? 'Hide' : 'Review'}
                    </button>
                    {/* KYC IS PER COMPANY OR PER INDIVIDUAL BROKER (Tej
                        2026-09-20): a member of a kind:'company' org is never
                        decided here — their whole company is, in the
                        Companies lens. Individual brokers, solo accounts and
                        legacy rows keep their per-person decision. */}
                    {u.orgId && u.orgKind === 'company' ? (
                      <button
                        type="button"
                        onClick={() => onOpenOrg?.(String(u.orgId))}
                        className="bg-navy-900 hover:bg-navy-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
                        data-testid="admin-user-kyc-company"
                        title="This person belongs to a company — approve or reject the COMPANY; every member inherits."
                      >
                        Company KYC →
                      </button>
                    ) : (
                      <>
                    <button
                      type="button"
                      disabled={working === `kyc-${k}`}
                      onClick={() => decideKyc(u, true)}
                      className="bg-success hover:brightness-110 text-white text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50 mr-1"
                      data-testid="admin-user-kyc-approve"
                    >
                      {working === `kyc-${k}` ? '…' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      disabled={working === `kyc-${k}`}
                      onClick={() => decideKyc(u, false)}
                      className="bg-paper border border-danger text-danger text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-danger/5 disabled:opacity-50"
                      data-testid="admin-user-kyc-reject"
                    >
                      Reject
                    </button>
                      </>
                    )}
                  </td>
                </tr>
                {openReview === k && (
                  <tr className="border-b border-[#f0f2f6] bg-cream/60" data-testid={`admin-user-review-${k}`}>
                    <td colSpan={6} className="py-3 px-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-xs">
                        <ReviewField label="Contact" value={(u.contactName as string) || (u.contactPerson as string)} />
                        <ReviewField label="Company" value={u.companyName as string} />
                        <ReviewField label="Phone" value={u.phone ? `${u.phone}${u.phoneVerified ? ' ✓ verified' : ' (unverified)'}` : null} />
                        <ReviewField label="Website" value={u.website as string} />
                        <ReviewField label="Address" value={typeof u.address === 'string' ? u.address : null} />
                        <ReviewField label="Heard via" value={u.hearAbout as string} />
                        {/* Which company this decision does NOT reach. Approving an
                            employee is not approving their employer, and rejecting
                            one member of a multi-member org suspends only their
                            membership row — the org button is the other decision. */}
                        <ReviewField
                          label="Organization"
                          value={u.orgId ? `${u.orgId} · ${u.orgRole || 'member'}${u.orgSeatStatus === 'suspended' ? ' · seat suspended' : ''}` : null}
                        />
                        {u.kycRejectionReason ? <ReviewField label="Last rejection" value={String(u.kycRejectionReason)} /> : null}
                      </div>
                      {/* Business WhatsApp (Tej 2026-09-21): set/replace the
                          number buyers WhatsApp this supplier on. Writes it as
                          the designate AND marks it admin-verified in one go —
                          the way to give a feed supplier (AVP/Navrang) a working
                          WhatsApp button. Full international format. */}
                      <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-wider text-soft font-semibold mb-1">Business WhatsApp</div>
                        <div className="flex items-center gap-2">
                          <input
                            type="tel"
                            inputMode="tel"
                            placeholder={u.phone ? String(u.phone) : '+1 415 555 0100'}
                            value={waDraft[keyFor(u)] ?? ''}
                            onChange={(e) => setWaDraft((d) => ({ ...d, [keyFor(u)]: e.target.value }))}
                            className="rounded-md border border-line bg-paper px-2 py-1.5 text-xs w-48"
                            data-testid="admin-user-whatsapp-input"
                          />
                          <button
                            type="button"
                            disabled={working === `wa-${keyFor(u)}` || !(waDraft[keyFor(u)] || '').trim()}
                            onClick={() => saveWhatsapp(u)}
                            className="rounded-md border border-gold bg-paper px-3 py-1.5 text-xs font-semibold text-gold-600 hover:bg-gold/5 disabled:opacity-50"
                            data-testid="admin-user-whatsapp-save"
                          >
                            {working === `wa-${keyFor(u)}` ? 'Saving…' : 'Set + verify'}
                          </button>
                        </div>
                        <span className="text-[11px] text-muted">Sets the buyer-facing WhatsApp number and marks it verified.</span>
                      </div>
                      {(u.kycDocuments?.length ?? 0) > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider text-soft font-semibold mb-1">Documents</div>
                          <div className="flex flex-wrap gap-2">
                            {u.kycDocuments!.map((d) => (
                              <button
                                key={d.objectPath}
                                type="button"
                                onClick={() => openDoc(d.objectPath)}
                                className="pill text-xs"
                                title={`Open ${d.fileName}`}
                              >
                                <span className="uppercase text-[9px] font-bold text-gold-600">{tileFor(d.docType).title}</span>
                                {d.fileName}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {(u.brokerReferences?.length ?? 0) > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider text-soft font-semibold mb-1">Broker references</div>
                          <div className="space-y-1">
                            {u.brokerReferences!.map((r, i) => (
                              <div key={i} className="text-xs text-ink">
                                <span className="font-semibold">{r.name}</span>
                                {r.company ? ` · ${r.company}` : ''}
                                {r.relationship ? ` · ${r.relationship}` : ''}
                                {r.email ? ` · ${r.email}` : ''}
                                {r.phone ? ` · ${r.phone}` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {u.brokerAffiliation ? (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider text-soft font-semibold mb-1">Business affiliation</div>
                          <div className="text-xs text-ink">{u.brokerAffiliation}</div>
                        </div>
                      ) : null}
                      {/* Danger zone (Tej 2026-09-20): delete from ASKA — the
                          shared self-service purge (orders retained). Company
                          members must be removed from their company first;
                          the server refuses admins + self. */}
                      <div className="mt-4 border-t border-danger/20 pt-3">
                        <button
                          type="button"
                          disabled={working === `del-${k}`}
                          onClick={() => deleteFromAska(u)}
                          className="rounded-md border border-danger bg-paper px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/5 disabled:opacity-50"
                          data-testid="admin-user-delete"
                        >
                          {working === `del-${k}` ? 'Deleting…' : 'Delete account from Aska'}
                        </button>
                        <span className="ml-2 text-[11px] text-muted">Removes profile, KYC files and sign-in. Orders are kept.</span>
                      </div>
                      {u.supplierDataSource?.method ? (
                        <div className="mt-3" data-testid="admin-user-datasource">
                          <div className="text-[10px] uppercase tracking-wider text-soft font-semibold mb-1">Stock connection</div>
                          <div className="text-xs text-ink space-y-1">
                            <div>
                              <span className="font-semibold">
                                {u.supplierDataSource.method === 'api' ? 'Supplier API'
                                  : u.supplierDataSource.method === 'excel' ? 'Excel / CSV sheet'
                                  : u.supplierDataSource.method === 'erp_push' ? 'ERP push'
                                  : 'Postponed ("do later")'}
                              </span>
                              {u.supplierDataSource.updatedAt ? ` · ${new Date(u.supplierDataSource.updatedAt).toLocaleDateString()}` : ''}
                            </div>
                            {u.supplierDataSource.method === 'api' && u.supplierDataSource.api?.url && (
                              <div className="break-all">
                                {u.supplierDataSource.api.url}
                                {/* The credential itself is no longer sent to this console —
                                    `GET /admin/users` used to ship the raw user document with
                                    `api.key` in it. We get `hasKey` + the last 4, which is all a
                                    reviewer chasing "the feed isn't authenticating" needs. */}
                                {u.supplierDataSource.api.hasKey
                                  ? ` · key ${u.supplierDataSource.api.keyMasked ?? 'on file'}`
                                  : ' · no key'}
                                {u.supplierDataSource.api.keyHeader ? ` · header ${u.supplierDataSource.api.keyHeader}` : ''}
                              </div>
                            )}
                            {u.supplierDataSource.lastTest && (
                              <div className={u.supplierDataSource.lastTest.ok ? 'text-success' : 'text-warning'}>
                                Last test: {u.supplierDataSource.lastTest.note}
                              </div>
                            )}
                            {u.supplierDataSource.sheet && (
                              <button
                                type="button"
                                onClick={() => openDoc(u.supplierDataSource!.sheet!.objectPath)}
                                className="pill text-xs"
                                title="Open sample sheet"
                              >
                                <span className="uppercase text-[9px] font-bold text-gold-600">sample</span>
                                {u.supplierDataSource.sheet.fileName}
                              </button>
                            )}
                            {u.supplierDataSource.sheetVerification && (
                              <div className={u.supplierDataSource.sheetVerification.ok ? 'text-success' : 'text-warning'}>
                                {u.supplierDataSource.sheetVerification.ok
                                  ? `Sheet verified — ${u.supplierDataSource.sheetVerification.matched.length} fields matched`
                                  : `Sheet missing: ${u.supplierDataSource.sheetVerification.missingRequired.join(', ')}`}
                              </div>
                            )}
                            {u.supplierDataSource.erp?.docsUrl && (
                              <div className="break-all">Docs: {u.supplierDataSource.erp.docsUrl}</div>
                            )}
                            {u.supplierDataSource.erp?.instructions && (
                              <button
                                type="button"
                                onClick={() => openDoc(u.supplierDataSource!.erp!.instructions!.objectPath)}
                                className="pill text-xs"
                                title="Open ERP instructions"
                              >
                                <span className="uppercase text-[9px] font-bold text-gold-600">erp</span>
                                {u.supplierDataSource.erp.instructions.fileName}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : null}
                      {!u.accountType && (u.kycDocuments?.length ?? 0) === 0 && (
                        <div className="mt-2 text-xs text-muted">No signup details on file (pre-wizard account).</div>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/** One label/value pair in the KYC review panel — blank values render a dash. */
function ReviewField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-wider text-soft font-semibold mr-1.5">{label}</span>
      <span className="text-ink">{value || '—'}</span>
    </div>
  )
}

